import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import { NoInternet } from '@/components/NoInternet';
import Login from '@/pages/Login';
import CustomerLogin from '@/pages/CustomerLogin';
import AgentLogin from '@/pages/AgentLogin';
import AgentPortal from '@/pages/AgentPortal';
import AgentRegister from '@/pages/AgentRegister';
import AdminDashboard from '@/pages/AdminDashboard';
import Properties from '@/pages/Properties';
import Customers from '@/pages/Customers';
import Billing from '@/pages/Billing';
import RedAlerts from '@/pages/RedAlerts';
import Leads from '@/pages/Leads';
import Expenses from '@/pages/Expenses';
import CustomerPortal from '@/pages/CustomerPortal';
import Reports from '@/pages/Reports';
import Settings from '@/pages/Settings';
import InstallmentPlans from '@/pages/InstallmentPlans';
import UserManagement from '@/pages/UserManagement';
import Roles from '@/pages/Roles';
import WhatsAppSettings from '@/pages/WhatsAppSettings';
import PartnerNetwork from '@/pages/PartnerNetwork';
import ReminderSettings from '@/pages/ReminderSettings';
import SmartImport from '@/pages/SmartImport';
import WebsiteManager from '@/pages/WebsiteManager';
import Salary from '@/pages/Salary';
import Agents from '@/pages/Agents';
import ActivityLogs from '@/pages/ActivityLogs';
import RevenueForecast from '@/pages/RevenueForecast';
// Public Website Pages
import HomePage from '@/pages/website/HomePage';
import AboutPage from '@/pages/website/AboutPage';
import PropertiesPage from '@/pages/website/PropertiesPage';
import PropertyDetail from '@/pages/website/PropertyDetail';
import ContactPage from '@/pages/website/ContactPage';
import BlogPage from '@/pages/website/BlogPage';
import BlogDetail from '@/pages/website/BlogDetail';
import SEOPage from '@/pages/website/SEOPage';
import ProjectsPage from '@/pages/website/ProjectsPage';
import GalleryPage from '@/pages/website/GalleryPage';
import '@/App.css';

// Role-based route protection
const ROLE_PERMISSIONS = {
  admin: ['dashboard', 'properties', 'customers', 'billing', 'leads', 'expenses', 'reports', 'forecast', 'settings', 'plans', 'users', 'roles', 'whatsapp', 'partner-network', 'reminders', 'smart-import', 'website', 'salary', 'agents', 'activity-logs'],
  sales: ['dashboard', 'leads', 'customers', 'billing', 'plans', 'settings', 'smart-import', 'agents'],
  billing: ['dashboard', 'customers', 'billing', 'forecast', 'plans', 'settings', 'smart-import', 'agents'],
  customer: ['customer_portal']
};

const hasPermission = (role, permission) => {
  if (role === 'admin') return true;
  const permissions = ROLE_PERMISSIONS[role] || [];
  return permissions.includes(permission);
};

const ProtectedRoute = ({ user, permission, children, redirectTo = '/admin' }) => {
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'customer') return <Navigate to="/customer" />;
  if (!hasPermission(user.role, permission)) return <Navigate to={redirectTo} />;
  return children;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [websiteEnabled, setWebsiteEnabled] = useState(false);

  useEffect(() => {
    // Check for admin user
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    
    // Check for customer
    const customerToken = localStorage.getItem('customer_token');
    const savedCustomer = localStorage.getItem('customer');
    if (customerToken && savedCustomer) {
      setCustomer(JSON.parse(savedCustomer));
    }
    
    // Check website enabled status - wait for it before showing app
    import('@/utils/api').then(({ default: api }) => {
      api.get('/public/website-status').then(r => {
        setWebsiteEnabled(r.data?.website_enabled === true);
      }).catch(() => setWebsiteEnabled(false))
      .finally(() => setLoading(false));
    });
  }, []);

  const handleLogin = (userData, token) => {
    setUser(userData);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleCustomerLogin = (customerData, token) => {
    setCustomer(customerData);
    localStorage.setItem('customer_token', token);
    localStorage.setItem('customer', JSON.stringify(customerData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const handleCustomerLogout = () => {
    setCustomer(null);
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer');
  };

  if (loading) {
    // Determine if website might be enabled (check URL path)
    const isWebsitePath = !window.location.pathname.startsWith('/admin') && 
                          !window.location.pathname.startsWith('/login') &&
                          !window.location.pathname.startsWith('/customer-login') &&
                          !window.location.pathname.startsWith('/agent-login');

    if (isWebsitePath) {
      // Website Splash - Dark Navy + Gold theme
      return (
        <div className="flex items-center justify-center min-h-screen bg-[#0B1120]" data-testid="website-splash">
          <div className="text-center">
            <div className="relative mb-6">
              <img 
                src="https://ganesahomestate.com/images/logo.png" 
                alt="Ganesa Homestate" 
                className="h-20 sm:h-28 w-auto mx-auto animate-pulse"
              />
              <div className="absolute -inset-4 bg-[#C9A84C]/10 rounded-full blur-2xl -z-10" />
            </div>
            <div className="flex items-center justify-center gap-1.5 mt-6">
              <div className="w-2 h-2 bg-[#C9A84C] rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
              <div className="w-2 h-2 bg-[#C9A84C] rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
              <div className="w-2 h-2 bg-[#C9A84C] rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
            </div>
            <p className="text-[#C9A84C]/60 text-xs mt-4 tracking-widest uppercase">Loading</p>
          </div>
        </div>
      );
    }

    // CRM Splash - Emerald theme
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-slate-50" data-testid="crm-splash">
        <div className="text-center">
          <div className="relative mb-5">
            <img 
              src="https://ganesahomestate.com/images/logo.png" 
              alt="Ganesa Homestate" 
              className="h-16 sm:h-20 w-auto mx-auto"
            />
          </div>
          <div className="w-48 h-1 bg-slate-200 rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full animate-[shimmer_1.5s_ease-in-out_infinite]" 
              style={{width: '40%', animation: 'shimmer 1.5s ease-in-out infinite'}} />
          </div>
          <p className="text-slate-400 text-xs mt-4 tracking-wider">Ganesa CRM</p>
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route
            path="/login"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/admin" />}
          />
          <Route
            path="/customer-login"
            element={!customer ? <CustomerLogin onCustomerLogin={handleCustomerLogin} /> : <Navigate to="/my-bookings" />}
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute user={user} permission="dashboard">
                <AdminDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing"
            element={
              <ProtectedRoute user={user} permission="billing">
                <Billing user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/red-alerts"
            element={
              <ProtectedRoute user={user} permission="billing">
                <RedAlerts user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/properties"
            element={
              <ProtectedRoute user={user} permission="properties">
                <Properties user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/customers"
            element={
              <ProtectedRoute user={user} permission="customers">
                <Customers user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/leads"
            element={
              <ProtectedRoute user={user} permission="leads">
                <Leads user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/expenses"
            element={
              <ProtectedRoute user={user} permission="expenses">
                <Expenses user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/plans"
            element={
              <ProtectedRoute user={user} permission="plans">
                <InstallmentPlans user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute user={user} permission="reports">
                <Reports user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/forecast"
            element={
              <ProtectedRoute user={user} permission="forecast">
                <RevenueForecast user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute user={user} permission="settings">
                <Settings user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute user={user} permission="users">
                <UserManagement user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute user={user} permission="roles">
                <Roles user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/whatsapp"
            element={
              <ProtectedRoute user={user} permission="whatsapp">
                <WhatsAppSettings user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reminders"
            element={
              <ProtectedRoute user={user} permission="reminders">
                <ReminderSettings user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/partner-network"
            element={
              <ProtectedRoute user={user} permission="partner-network">
                <PartnerNetwork user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/smart-import"
            element={
              <ProtectedRoute user={user} permission="smart-import">
                <SmartImport user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/website"
            element={
              <ProtectedRoute user={user} permission="website">
                <WebsiteManager user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/salary"
            element={
              <ProtectedRoute user={user} permission="salary">
                <Salary user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/agents"
            element={
              <ProtectedRoute user={user} permission="agents">
                <Agents user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/activity-logs"
            element={
              <ProtectedRoute user={user} permission="activity-logs">
                <ActivityLogs user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent-login"
            element={<AgentLogin />}
          />
          <Route
            path="/agent-portal"
            element={<AgentPortal />}
          />
          <Route
            path="/agent/register"
            element={<AgentRegister />}
          />
          <Route
            path="/agent/register/:referralCode"
            element={<AgentRegister />}
          />
          <Route
            path="/my-bookings"
            element={customer ? <CustomerPortal customer={customer} onLogout={handleCustomerLogout} /> : <Navigate to="/customer-login" />}
          />
          <Route
            path="/customer"
            element={<Navigate to="/customer-login" />}
          />
          {/* Public Website Routes - only when enabled */}
          <Route path="/" element={websiteEnabled ? <HomePage /> : <Navigate to="/login" />} />
          <Route path="/about" element={websiteEnabled ? <AboutPage /> : <Navigate to="/login" />} />
          <Route path="/properties" element={websiteEnabled ? <PropertiesPage /> : <Navigate to="/login" />} />
          <Route path="/properties/:id" element={websiteEnabled ? <PropertyDetail /> : <Navigate to="/login" />} />
          <Route path="/contact" element={websiteEnabled ? <ContactPage /> : <Navigate to="/login" />} />
          <Route path="/blog" element={websiteEnabled ? <BlogPage /> : <Navigate to="/login" />} />
          <Route path="/blog/:slug" element={websiteEnabled ? <BlogDetail /> : <Navigate to="/login" />} />
          <Route path="/location/:slug" element={websiteEnabled ? <SEOPage /> : <Navigate to="/login" />} />
          <Route path="/projects" element={websiteEnabled ? <ProjectsPage /> : <Navigate to="/login" />} />
          <Route path="/gallery" element={websiteEnabled ? <GalleryPage /> : <Navigate to="/login" />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
      <NoInternet />
    </div>
    </HelmetProvider>
  );
}

export default App;
